import React, { useEffect, useState, useCallback } from 'react'
import useDrivePicker from 'react-google-drive-picker'
import { googleClientId, googlePickerDeveloperToken, googleDriveAppId } from 'token'
import { Button } from 'react-bootstrap'
import axios from 'axios'
import { getAxiosDefaultConfig } from 'utils'
import { getGoogleTokenUrl, getGoogleDriveTokenUrl, googleDriveAuthUrl } from 'urls'
import { linkAccount } from 'account'

// Check if the app is running inside an iframe
const isInIframe = () => {
  try {
    return window !== window.top
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions, we're in an iframe
    return true
  }
}

interface FilePickerProps {
  setFolderInfo?: (name: string, id: string) => void
  folderName?: string
  viewId: string
  setFileInfo?: (name: string, id: string, accessToken?: string) => void
  fileName?: string
  selectedEmail?: string
  hasGoogleDrive?: boolean
}

function FilePicker({ setFolderInfo, folderName, viewId, setFileInfo, fileName, selectedEmail, hasGoogleDrive }: FilePickerProps) {
  const [openPicker, authResponse] = useDrivePicker()
  const [isIframeContext] = useState(isInIframe)
  const [popupWindow, setPopupWindow] = useState<Window | null>(null)
  const [isWaitingForSelection, setIsWaitingForSelection] = useState(false)

  // Handle messages from popup window
  const handleMessage = useCallback((event: MessageEvent) => {
    // Validate the message origin (should be same origin)
    if (event.origin !== window.location.origin) {
      return
    }

    const { type, data } = event.data || {}

    if (type === 'GOOGLE_PICKER_SELECTION') {
      setIsWaitingForSelection(false)
      if (data?.action === 'picked' && data?.docs?.[0]) {
        if (viewId === 'FOLDERS') {
          setFolderInfo?.(data.docs[0].name, data.docs[0].id)
        } else {
          setFileInfo?.(data.docs[0].name, data.docs[0].id, data.accessToken)
        }
      }
      // Close popup after selection
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close()
      }
      setPopupWindow(null)
    } else if (type === 'GOOGLE_PICKER_CANCEL') {
      setIsWaitingForSelection(false)
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close()
      }
      setPopupWindow(null)
    } else if (type === 'GOOGLE_PICKER_READY') {
      // Popup is ready, send configuration
      if (popupWindow && !popupWindow.closed) {
        sendPickerConfig(popupWindow)
      }
    }
  }, [viewId, setFolderInfo, setFileInfo, popupWindow])

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [handleMessage])

  // Clean up popup window on unmount
  useEffect(() => {
    return () => {
      if (popupWindow && !popupWindow.closed) {
        popupWindow.close()
      }
    }
  }, [popupWindow])

  // Send picker configuration to popup
  const sendPickerConfig = async (popup: Window) => {
    try {
      const res = await axios.get(`${getGoogleDriveTokenUrl}?email=${selectedEmail}`, getAxiosDefaultConfig())
      const token = res.data.access_token

      console.log('[FilePicker] popup picker config', {
        clientId: googleClientId,
        appId: googleDriveAppId || '(unset)',
        developerKeyPresent: Boolean(googlePickerDeveloperToken),
        selectedEmail,
      })
      popup.postMessage({
        type: 'GOOGLE_PICKER_CONFIG',
        config: {
          clientId: googleClientId,
          developerKey: googlePickerDeveloperToken,
          appId: googleDriveAppId || undefined,
          viewId: viewId,
          token: token,
          customScopes: ["email", "profile", "https://www.googleapis.com/auth/drive.file", "https://www.googleapis.com/auth/spreadsheets"],
          showUploadView: true,
          showUploadFolders: true,
          setSelectFolderEnabled: true,
          supportDrives: true,
          multiselect: false,
        }
      }, window.location.origin)
    } catch (error) {
      console.error('Error getting Google Drive token:', error)
      setIsWaitingForSelection(false)
    }
  }

  // Open picker in popup window (for iframe context)
  const handleOpenPickerPopup = async () => {
    if (!hasGoogleDrive) {
      linkAccount({
        channel: 'google_drive',
      })
      return
    }

    // Close any existing popup
    if (popupWindow && !popupWindow.closed) {
      popupWindow.close()
    }

    setIsWaitingForSelection(true)

    // Calculate popup window size and position
    const width = 800
    const height = 600
    const left = (window.screen.width - width) / 2
    const top = (window.screen.height - height) / 2

    // Create the popup window with our picker page
    const popup = window.open(
      '/file-picker-popup',
      'GoogleDrivePicker',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    if (popup) {
      setPopupWindow(popup)

      // Monitor popup close
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed)
          setIsWaitingForSelection(false)
          setPopupWindow(null)
        }
      }, 500)
    } else {
      // Popup was blocked
      setIsWaitingForSelection(false)
      alert('Please allow popups for this site to use the file picker.')
    }
  }

  // Original picker handler (for non-iframe context)
  const handleOpenPicker = async () => {
    if (!hasGoogleDrive) {
      linkAccount({
        channel: 'google_drive',
      })
      return
    }
    let res = await axios.get(`${getGoogleDriveTokenUrl}?email=${selectedEmail}`, getAxiosDefaultConfig())

    const token = res.data.access_token
    console.log('[FilePicker] opening picker', {
      clientId: googleClientId,
      appId: googleDriveAppId || '(unset)',
      developerKeyPresent: Boolean(googlePickerDeveloperToken),
      selectedEmail,
    })
    openPicker({
      clientId: googleClientId,
      developerKey: googlePickerDeveloperToken,
      appId: googleDriveAppId || undefined,
      viewId: viewId,
      token: token,
      customScopes: ["email","profile","https://www.googleapis.com/auth/drive.file","https://www.googleapis.com/auth/spreadsheets"],
      showUploadView: true,
      showUploadFolders: true,
      setSelectFolderEnabled: true,
      supportDrives: true,
      multiselect: false,
      callbackFunction: (data: any) => {
        // Picker callback fires for 'loaded', 'picked', and 'cancel'. Only
        // 'picked' has data.docs populated — guard before indexing.
        if (data.action !== 'picked' || !data.docs?.[0]) return
        const doc = data.docs[0]
        if (viewId === 'FOLDERS') {
          setFolderInfo?.(doc.name, doc.id)
        } else {
          // Use the token the picker actually authenticated with — if the user
          // switched accounts in the picker UI, this may differ from `token`.
          const usedToken = (data as any).accessToken || (authResponse && (authResponse as any).access_token) || token
          setFileInfo?.(doc.name, doc.id, usedToken)
        }
      },
    })
  }

  // Choose which handler to use based on context
  const onPickerClick = isIframeContext ? handleOpenPickerPopup : handleOpenPicker

  // Button text based on state
  const getButtonText = () => {
    if (isWaitingForSelection) {
      return (
        <>
          <span className="fa fa-spinner fa-spin tw-mr-2" />
          Waiting for selection...
        </>
      )
    }

    if (viewId === 'FOLDERS') {
      return isIframeContext ? (
        <>
          <span className="fa fa-external-link tw-mr-2" />
          Choose Google Folder
        </>
      ) : 'Choose Google Folder'
    } else {
      return isIframeContext ? (
        <>
          <span className="fa fa-external-link tw-mr-2" />
          Choose Google File
        </>
      ) : 'Choose Google File'
    }
  }

  let renderView = null

  if (viewId === 'FOLDERS') {
    renderView = folderName ? (
      <>
        <div>Selected Folder: {folderName}</div>
        <div
          onClick={() => !isWaitingForSelection && onPickerClick()}
          style={{
            textDecoration: 'underline',
            fontSize: 'smaller',
            color: isWaitingForSelection ? '#999' : '#0d6efd',
            cursor: isWaitingForSelection ? 'not-allowed' : 'pointer'
          }}
        >
          {isWaitingForSelection ? 'Waiting...' : 'Choose another folder...'}
        </div>
      </>
    ) : (
      <Button onClick={onPickerClick} disabled={isWaitingForSelection}>
        {getButtonText()}
      </Button>
    )
  } else {
    renderView = fileName ? (
      <>
        <div>Selected File: {fileName}</div>
        <div
          onClick={() => !isWaitingForSelection && onPickerClick()}
          style={{
            textDecoration: 'underline',
            fontSize: 'smaller',
            color: isWaitingForSelection ? '#999' : '#0d6efd',
            cursor: isWaitingForSelection ? 'not-allowed' : 'pointer'
          }}
        >
          {isWaitingForSelection ? 'Waiting...' : 'Choose another file...'}
        </div>
      </>
    ) : (
      <Button onClick={onPickerClick} disabled={isWaitingForSelection}>
        {getButtonText()}
      </Button>
    )
  }

  return (
    <div>
      {isIframeContext && (
        <div className="tw-text-xs tw-text-gray-500 tw-mb-2">
          <span className="fa fa-info-circle tw-mr-1" />
          Opens in a new window
        </div>
      )}
      {renderView}
    </div>
  )
}
export default FilePicker
